// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import loadBrandsAndSolutions from "@guestsupply/App/Handlers/XNG/BrandsAndSolutions";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import MainNavigationItem from "@guestsupply/Overrides/Components/MainNavigationItem";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import Search from "@guestsupply/XDL/Icons/Search";
import { color, parseBoolean, xScreen } from "@guestsupply/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { setMainNavigation } from "@insite/client-framework/Components/ShellHoleConnect";
import Logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StylesContext } from "@insite/content-library/additionalStyles";
import NavigationDrawer from "@insite/content-library/Components/NavigationDrawer";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import SearchInput, { SearchInputStyles } from "@insite/content-library/Widgets/Header/SearchInput";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { MenuPresentationProps } from "@insite/mobius/Menu";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { PopoverPresentationProps, PositionStyle } from "@insite/mobius/Popover";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as cssLinter from "css";
import isEqual from "lodash/isEqual";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    showQuickOrder = "showQuickOrder",
    openParentPages = "openParentPages",
    customCSS = "customCSS",
    // xng-changes
    showFeaturedPartners = "showFeaturedPartners",
    showAllSolutions = "showAllSolutions",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.showQuickOrder]: boolean;
        [fields.openParentPages]: boolean;
        [fields.showFeaturedPartners]: boolean;
        [fields.showAllSolutions]: boolean;
        [fields.customCSS]: string;
    };
    isCompactHeaderFlyoutMenu: boolean;
    flyoutCustomCSS?: string;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    isCompactHeaderMainNavigation: boolean;
    сompactHeaderMainNavigationCustomCSS?: string;
}

interface LinkModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        linkType: "Link" | "MegaMenu" | "CascadingMenu";
        overrideTitle: string;
        title: string;
        numberOfColumns: number;
        maxDepth: number;
    };
}

export interface MappedLink {
    title: string;
    url: string;
    type?: string;
    excludeFromNavigation?: boolean;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
}
// XNG-CHANGES : Adding navMappedLinks and categoryIdsToLoad outsitde mapStateToProps to prevent un-needed re-renders
let navMappedLinks: MappedLink[] = [];
let navCategoryIdsToLoad: Dictionary<number> = {};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const categoryIdsToLoad: Dictionary<number> = {};

    const {
        fields: { links = [] },
    } = ownProps;
    const mappedLinks = [];
    for (const link of links) {
        const {
            fields: { linkType, destination, openInNewWindow, overrideTitle, numberOfColumns, maxDepth, title },
        } = link;
        const { type, value } = destination;

        const depthToLoad = linkType === "MegaMenu" ? 3 : maxDepth;

        let mappedLink: MappedLink | undefined;

        if (type === "Url") {
            mappedLink = {
                url: value,
                title,
            };
        } else if (type === "Page") {
            const readonlyPageLink = getPageLinkByNodeId(state, value);
            if (readonlyPageLink) {
                mappedLink = { ...readonlyPageLink };
                if (overrideTitle) {
                    mappedLink.title = overrideTitle;
                }
            }
        } else if (type === "Category") {
            mappedLink = setupCategoryLink(
                state,
                value,
                categoryIdsToLoad,
                depthToLoad,
                overrideTitle,
                state.data.categories.categoryIdsCalled,
            );
        }

        if (!mappedLink) {
            continue;
        }

        mappedLink.openInNewWindow = openInNewWindow;
        mappedLink.numberOfColumns = numberOfColumns;
        mappedLink.maxDepth = maxDepth;

        if (linkType === "MegaMenu" || linkType === "CascadingMenu") {
            mappedLink.childrenType = linkType;
        }

        mappedLinks.push(mappedLink);
    }
    // XNG-CHANGES
    if (!isEqual(navMappedLinks, mappedLinks)) {
        navMappedLinks = mappedLinks;
    }
    // XNG-CHANGES
    if (!isEqual(navCategoryIdsToLoad, categoryIdsToLoad)) {
        navCategoryIdsToLoad = categoryIdsToLoad;
    }

    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const orderSettings = getSettingsCollection(state).orderSettings;
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const isVmiAdmin = session.userRoles?.toLowerCase().indexOf("vmi_admin") !== -1;
    const isVmiUser = session.userRoles?.toLowerCase().indexOf("vmi_user") !== -1;

    const isEducationCustomer = parseBoolean(session.properties?.isEducationCustomer);
    return {
        links: navMappedLinks,
        quickOrderLink: getPageLinkByPageType(state, "QuickOrderPage"),
        categoryIdsToLoad: navCategoryIdsToLoad,
        allowQuickOrder: orderSettings.allowQuickOrder,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        categoryIdsCalled: state.data.categories.categoryIdsCalled,
        displayChangeCustomerLink: session?.displayChangeCustomerLink,
        displayVmiNavigation:
            currentMode === "Vmi" && orderSettings.vmiEnabled && isAuthenticated && (isVmiAdmin || isVmiUser),
        displayModeSwitch: isAuthenticated && orderSettings.vmiEnabled && (isVmiAdmin || isVmiUser),
        currentMode,
        // XNG-CHANGES
        // vmiPageLinks: isVmiAdmin
        //     ? [
        //         getPageLinkByPageType(state, "VmiDashboardPage"),
        //         getPageLinkByPageType(state, "VmiLocationsPage"),
        //         getPageLinkByPageType(state, "VmiUsersPage"),
        //         getPageLinkByPageType(state, "VmiBinsPage"),
        //         getPageLinkByPageType(state, "VmiReportingPage"),
        //         getPageLinkByPageType(state, "VmiOrderHistoryPage"),
        //     ]
        //     : isVmiUser
        //         ? [
        //             getPageLinkByPageType(state, "VmiDashboardPage"),
        //             getPageLinkByPageType(state, "VmiLocationsPage"),
        //             getPageLinkByPageType(state, "VmiBinsPage"),
        //             getPageLinkByPageType(state, "VmiReportingPage"),
        //             getPageLinkByPageType(state, "VmiOrderHistoryPage"),
        //         ]
        //         : [],
        VmiDashboardPage: getPageLinkByPageType(state, "VmiDashboardPage"),
        VmiLocationsPage: getPageLinkByPageType(state, "VmiLocationsPage"),
        VmiUsersPage: getPageLinkByPageType(state, "VmiUsersPage"),
        VmiBinsPage: getPageLinkByPageType(state, "VmiBinsPage"),
        VmiReportingPage: getPageLinkByPageType(state, "VmiReportingPage"),
        VmiOrderHistoryPage: getPageLinkByPageType(state, "VmiOrderHistoryPage"),

        xBrandsAndSolutions: (state as CustomAppState).xBrandsAndSolutions,
        isEducationCustomer,

        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
        currentBillTo: getCurrentBillToState(state).value,
        currentPage: getCurrentPage(state).type,
    };
};

const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overrideTitle: string,
    categoryIdsCalled: string[],
) => {
    let mappedLink: MappedLink | undefined;

    if (!value) {
        return;
    }

    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);
    if (
        (depthLoaded < maxDepth || (!subCategoryIds && !categoryIdsCalled?.includes(value))) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            mappedLink = {
                url: "",
                title: overrideTitle || translate("Products"),
            };
        } else {
            const categoryState = getCategoryState(state, value);
            if (!categoryState.value) {
                if (
                    !categoryState.isLoading &&
                    !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
                ) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }
            mappedLink = {
                url: categoryState.value.path,
                title: overrideTitle || categoryState.value.shortDescription,
            };
        }

        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state);
    }

    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];
    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }
        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
        };
        mappedLink.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state);
    }
};

const mapDispatchToProps = {
    loadCategories,
    // XNG-Changes
    loadBrandsAndSolutions,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface State {
    selectedLinkIndex?: number;
    mobileSearchModalIsOpen: boolean;
}

export interface MainNavigationStyles {
    menuHeight: number;
    container?: InjectableCss;
    itemWrapper?: InjectableCss;
    quickOrderItemWrapper?: InjectableCss;
    menuItem?: InjectableCss;
    menuItemIcon?: IconProps;
    menuItemTypography?: TypographyPresentationProps;
    cascadingMenu?: MenuPresentationProps;
    megaMenu?: PopoverPresentationProps;
    megaMenuGridContainer?: GridContainerProps;
    megaMenuGridItem?: GridItemProps;
    megaMenuHeading?: LinkPresentationProps;
    megaMenuLink?: LinkPresentationProps;
    mobileWrapper?: InjectableCss;
    compactFlyoutMenu?: InjectableCss;
    mobileMenuWrapper?: InjectableCss;
    mobileSearchButton?: ButtonPresentationProps;
    mobileSearchWrapper?: InjectableCss;
    mobileSearchModal?: ModalPresentationProps;
    mobileSearchInputWrapper?: InjectableCss;
    mobileSearchInputStyles?: SearchInputStyles;
    mobileSearchModalCloseIcon?: IconProps;
    // XNG-XHANGES
    itemsContainerStyledWrapper?: InjectableCss;
}

export const mainNavigationStyles: MainNavigationStyles = {
    menuHeight: 50,
    container: {
        css: css`
            color: ${getColor("common.background")};
            display: flex;
            justify-content: space-between;
        `,
    },
    itemWrapper: {
        css: css`
            position: relative;
            overflow: visible;
            display: flex;
            &:hover span + div {
                display: block;
            }
            padding: 0 16px;
            &:last-child {
                padding-right: 0;
            }
            &:first-child {
                padding-left: 0;
            }
        `,
    },
    quickOrderItemWrapper: {
        css: css`
            margin-left: auto;
            margin-right: 0;
        `,
    },
    megaMenuGridItem: {
        css: css`
            display: block;
        `,
    },
    megaMenuHeading: {
        css: css`
            font-weight: bold;
            margin-bottom: 8px;
        `,
    },
    menuItem: {
        css: css`
            display: flex;
            align-items: center;
            height: 50px;
            padding: 16px;
            &&& {
                &:hover,
                &:active {
                    background-color: ${getColor("common.ui2")};
                    box-shadow: inset 0 2px 0 0 ${getColor("common.ui9")};
                    margin-top: -1px;
                    padding-top: 17px;
                    height: 51px;
                    span {
                        color: ${getColor("common.ui9")};
                    }
                }
            }
            ${xScreen("lte", 1115)} {
                padding: 15px 8px 16px;
            }
        `,
    },
    menuItemIcon: {
        size: 11,
        color: "text.link",
        css: css`
            margin: 0 0 5px 10px;

            &&& {
                svg {
                    color: ${getColor("text.link")};
                }
            }
        `,
    },
    menuItemTypography: {
        variant: "headerSecondary",
        color: "text.main",
        size: 16,
        weight: 500,
        css: css`
            line-height: 17px;
            display: flex;
            align-items: baseline;
        `,
    },
    megaMenu: {
        transitionDuration: "short",
        contentBodyProps: {
            _height: "1200px",
            css: css`
                padding: 20px 30px;
            `,
        },
    },
    mobileSearchButton: {
        size: 48,
        buttonType: "solid",
        color: "secondary",
        css: css`
            padding: 0 10px;
        `,
    },
    mobileWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    compactFlyoutMenu: {
        css: css`
            & button {
                background: transparent;
                border: none;
                &:hover {
                    background: ${color("common.ui7", 0.3)};
                }

                svg {
                    color: ${props => resolveColor(props.theme.header.linkColor, props.theme)};
                }
            }
        `,
    },
    mobileSearchWrapper: {
        css: css`
            text-align: right;
        `,
    },
    mobileMenuWrapper: {
        css: css`
            text-align: right;
        `,
    },
    mobileSearchInputWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    mobileSearchInputStyles: {
        input: {
            cssOverrides: {
                formField: css`
                    width: 100%;
                `,
            },
        },
        popover: {
            wrapperProps: {
                css: css`
                    width: calc(100% - 45px);
                `,
            },
        },
        popoverContentBody: {
            as: "div",
            _height: "100%",
            css: css`
                box-shadow: none;
            `,
        },
        autocompleteWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },
        autocompleteColumnWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 100%;
                padding: 0 50px 0 30px;
            `,
        },
        searchHistoryStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    padding: 0 50px 15px 30px;
                `,
            },
        },
        autocompleteProductsStyles: {
            titleLink: {
                css: css`
                    width: 100%;
                `,
            },
            erpNumberText: {
                css: css`
                    width: 100%;
                    text-align: left;
                    margin: 5px 0 0 0;
                `,
            },
        },
    },
    mobileSearchModalCloseIcon: {
        src: X,
        size: 18,
        css: css`
            margin-left: 10px;
        `,
    },
};

const defaultCustomCss = `.mobile-wrapper {
}

.mobile-menu-wrapper {
}

.menu-trigger-button {
}

.menu-trigger-button-icon {
}

.drawer {
}

.drawer-section-wrapper {
}

.panel-menu {
}

.main-navigation-row {
}

.panel-section-wrapper {
}

.main-navigation-row-icon {
}

.user-row-text {
}

.punch-out-navigation-row-icon {
}

.punch-out-user-row-text {
}

.main-navigation-row-text {
}

.logo-links-row {
}

.logo-link-title {
}

.language-image {
}

.menu-row-icon {
}

.change-customer-row {
}

.change-customer-row-container {
}

.fulfillment-method-grid-item {
}

.addresses-grid-item {
}

.pick-up-address-grid-item {
}

.apply-button-grid-item {
}

.sign-out-row {
}

.sign-out-row-text {
}

.mobile-search-wrapper {
}

.mobile-search-button {
}

.mobile-search-button-icon {
}

.container {
}

.item-wrapper {
}

.cascading-menu {
}

.menu-item {
}

.mega-menu {
}

.mega-menu-grid-container {
}

.mega-menu-grid-item {
}

.mega-menu-heading-link {
}

.mega-menu-link {
}

.quick-order-item-wrapper {
}

.quick-order-link {
}

.vmi-page-link {
}

.mobile-search-modal {
}`;

export class MainNavigation extends React.Component<Props, State> {
    container = React.createRef<HTMLDivElement>();
    mobileSearchInput = React.createRef<HTMLInputElement>();
    declare context: React.ContextType<typeof StylesContext>;

    constructor(props: Props) {
        super(props);

        this.state = {
            mobileSearchModalIsOpen: false,
        };
    }

    UNSAFE_componentWillMount(): void {
        this.loadCategoriesIfNeeded();

        setMainNavigation({
            close: () => {
                this.setState({
                    selectedLinkIndex: undefined,
                });
            },
            openMenu: (index: number) => {
                this.setState({
                    selectedLinkIndex: index,
                });
            },
        });
    }
    componentDidMount(): void {
        if (
            !this.props.xBrandsAndSolutions?.isLoading &&
            !Object.keys(this.props.xBrandsAndSolutions.brandsAndSolutions).length &&
            !this.props.isEducationCustomer
        ) {
            this.props.loadBrandsAndSolutions({});
        }
    }
    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (!isEqual(this.props.categoryIdsToLoad, prevProps.categoryIdsToLoad)) {
            this.loadCategoriesIfNeeded(true);
        }
    }

    loadCategoriesIfNeeded(isComponentDidUpdate = false) {
        const { categoryIdsToLoad } = this.props;
        for (const categoryId of Object.keys(categoryIdsToLoad)) {
            if (!this.props.categoryErrorStatusCodeById?.[categoryId]) {
                const maxDepth = categoryIdsToLoad[categoryId];
                if (!this.props.categoryIdsCalled?.includes(categoryId) || isComponentDidUpdate) {
                    this.props.loadCategories(getParameter(categoryId, maxDepth));
                }
            }
        }
    }

    getMobileSearchAutocompletePosition = (element: React.RefObject<HTMLUListElement>) => {
        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top = rect.top + rect.height;
            positionStyle.left = 0;
            positionStyle.width = `${document.documentElement.clientWidth}px`;
            positionStyle.height = `${document.documentElement.clientHeight - positionStyle.top}px`;
        }

        return positionStyle;
    };

    mobileSearchButtonClickHandler = () => {
        this.setState({ mobileSearchModalIsOpen: true }, () => {
            setTimeout(() => {
                if (this.mobileSearchInput?.current) {
                    this.mobileSearchInput.current.focus();
                    this.mobileSearchInput.current.click();
                }
            }, 250);
        });
    };

    mobileSearchModalCloseHandler = () => {
        this.setState({ mobileSearchModalIsOpen: false });
    };

    // xng-change: add google event
    getDataLayerEvent = (title: string, url?: string | undefined) => {
        const dataLayer = getDataLayer();
        const { xGoogleAnalytics, currentPage, currentBillTo } = this.props;

        if (xGoogleAnalytics.includeNavigationBarEvent) {
            const googleAnalyticsData = {
                event: GOOGLE_EVENTS.NAVIGATION_BAR,
                click_text: title,
                page_path: url || currentPage,
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    render() {
        const {
            links,
            quickOrderLink,
            // XNG-CHANGES
            fields: { showQuickOrder, showFeaturedPartners, showAllSolutions, openParentPages, customCSS },
            id,
            allowQuickOrder,
            isCompactHeaderFlyoutMenu,
            flyoutCustomCSS,
            showFlyoutPricingToggle,
            showFlyoutInventoryToggle,
            isCompactHeaderMainNavigation,
            сompactHeaderMainNavigationCustomCSS,
            // vmiPageLinks,
            displayVmiNavigation,
            displayModeSwitch,
            currentMode,
            // XNG-CHANGES
            xBrandsAndSolutions,
            VmiDashboardPage,
            VmiLocationsPage,
            VmiUsersPage,
            VmiBinsPage,
            VmiReportingPage,
            VmiOrderHistoryPage,
            isEducationCustomer,
        } = this.props;
        // XNG-CHANGES
        const vmiPageLinks = [
            VmiDashboardPage,
            VmiLocationsPage,
            VmiUsersPage,
            VmiBinsPage,
            VmiReportingPage,
            VmiOrderHistoryPage,
        ];

        const { selectedLinkIndex } = this.state;

        const showQuickOrderLink = showQuickOrder && allowQuickOrder;
        const mainNavContextStyles = this?.context?.styles?.mainNavigation ?? {};
        const styles = mergeToNew(mainNavigationStyles, mainNavContextStyles);

        // XNG-CHANGES
        const brands = xBrandsAndSolutions.brandsAndSolutions?.item1;
        const solutions = xBrandsAndSolutions.brandsAndSolutions?.item2;

        if (isCompactHeaderFlyoutMenu) {
            const flyoutCustomCssWrapper = {
                css: css`
                    ${flyoutCustomCSS}
                `,
            };

            return (
                <StyledWrapper {...flyoutCustomCssWrapper}>
                    <StyledWrapper className="flyout-menu-wrapper" {...styles.compactFlyoutMenu}>
                        <NavigationDrawer
                            links={links}
                            showQuickOrder={showQuickOrderLink}
                            quickOrderLink={quickOrderLink}
                            displayVmiNavigation={displayVmiNavigation}
                            vmiPageLinks={vmiPageLinks}
                            displayModeSwitch={displayModeSwitch}
                            currentMode={currentMode}
                            isCompactHeaderFlyout={true}
                            showFlyoutPricingToggle={showFlyoutPricingToggle}
                            showFlyoutInventoryToggle={showFlyoutInventoryToggle}
                            allowOpenParentLinks={openParentPages}
                            {...({ showAllSolutions, showFeaturedPartners, xBrandsAndSolutions } as any)}
                            // xng-change: add google event
                            handleClickedLink={(title: string, url: string) => this.getDataLayerEvent(title, url)}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            );
        }

        if (isCompactHeaderMainNavigation) {
            const mainNavigationCustomCSSWrapper = {
                css: css`
                    ${сompactHeaderMainNavigationCustomCSS}
                `,
            };

            return (
                <StyledWrapper id="main-navigation-container" {...mainNavigationCustomCSSWrapper}>
                    {displayVmiNavigation && (
                        <StyledWrapper className="container" {...styles.container} ref={this.container}>
                            <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                {vmiPageLinks.map(
                                    link =>
                                        link && (
                                            <Link
                                                className="vmi-page-link"
                                                key={link.id}
                                                typographyProps={styles.menuItemTypography}
                                                color={styles.menuItemTypography?.color}
                                                {...styles.menuItem}
                                                id={link.id}
                                                href={link.url}
                                                aria-label={link.title}
                                                // xng-change: add google event
                                                onClick={() => this.getDataLayerEvent(link.title, link.url)}
                                            >
                                                {link.title}
                                            </Link>
                                        ),
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {!displayVmiNavigation && !isEducationCustomer && (
                        <StyledWrapper className="container" {...styles.container} ref={this.container}>
                            <StyledWrapper {...styles}>
                                {links.map((link, index) => {
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <StyledWrapper className="item-wrapper" {...styles.itemWrapper} key={index}>
                                            <MainNavigationItem
                                                index={index}
                                                link={link}
                                                styles={styles}
                                                container={this.container}
                                                isOpen={index === selectedLinkIndex}
                                                displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                                // xng-change: add google event
                                                handleClickedLink={() => this.getDataLayerEvent(link.title, link.url)}
                                            />
                                        </StyledWrapper>
                                    );
                                })}
                                {/* XNG-CHANGES: Adding brand and solutions menu items */}
                                {showFeaturedPartners && brands && (
                                    <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                        <MainNavigationItem
                                            index="brand"
                                            link={{
                                                title: brands.brandPageTitle,
                                                url: brands.brandPageUrl,
                                                childrenType: "CascadingMenu",
                                                children: brands.headerFeaturedBrands,
                                                headerContent: brands.headerFeaturedBrands,
                                            }}
                                            marketingData={{
                                                marketingGeneralPromotionTitle: brands.marketingGeneralPromotionTitle,
                                                marketingGeneralPromoContent: brands.marketingGeneralPromoContent,
                                                marketingGeneralPromoImage: brands.marketingGeneralPromoImage,
                                                marketingFeaturedProducts: brands.marketingFeaturedProducts,
                                            }}
                                            styles={styles}
                                            container={this.container}
                                            displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                            {...({ pageType: "BrandsPage" } as any)}
                                            // xng-change: add google event
                                            handleClickedLink={(title: string, url: string) =>
                                                this.getDataLayerEvent(title, url)
                                            }
                                        />
                                    </StyledWrapper>
                                )}
                                {showAllSolutions && solutions && (
                                    <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                        <MainNavigationItem
                                            index="solutions"
                                            link={{
                                                title: solutions?.solutionPageTitle ?? translate("Our Solutions"),
                                                url: solutions.solutionPageUrl,
                                                childrenType: "CascadingMenu",
                                                children: solutions.headerSolutions,
                                                headerContent: solutions.headerSolutions,
                                            }}
                                            marketingData={{
                                                marketingGeneralPromotionTitle:
                                                    solutions.marketingGeneralPromotionTitle,
                                                marketingGeneralPromoContent: solutions.marketingGeneralPromoContent,
                                                marketingGeneralPromoImage: solutions.marketingGeneralPromoImage,
                                                marketingFeaturedProducts: solutions.marketingFeaturedProducts,
                                            }}
                                            styles={styles}
                                            container={this.container}
                                            displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                            {...({ pageType: "SolutionsPage" } as any)}
                                            // xng-change: add google event
                                            handleClickedLink={(title: string, url: string) => {
                                                this.getDataLayerEvent(title, url);
                                            }}
                                        />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles}>
                                {showQuickOrderLink && quickOrderLink && (
                                    <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                        <StyledWrapper
                                            className="quick-order-item-wrapper"
                                            {...styles.quickOrderItemWrapper}
                                        >
                                            <Link
                                                className="quick-order-link"
                                                typographyProps={styles.menuItemTypography}
                                                color={styles.menuItemTypography?.color}
                                                {...styles.menuItem}
                                                id="quickOrder"
                                                href={quickOrderLink.url}
                                                aria-label={quickOrderLink.title}
                                                // xng-change: add google event
                                                onClick={() =>
                                                    this.getDataLayerEvent(quickOrderLink.title, quickOrderLink.url)
                                                }
                                            >
                                                {quickOrderLink.title}
                                            </Link>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            );
        }

        const customCssWrapper = {
            css: css`
                ${customCSS}
            `,
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper className="mobile-wrapper" {...styles.mobileWrapper}>
                    <Hidden className="mobile-menu-wrapper" above="md" {...styles.mobileMenuWrapper}>
                        <NavigationDrawer
                            links={links}
                            showQuickOrder={showQuickOrderLink}
                            quickOrderLink={quickOrderLink}
                            displayVmiNavigation={displayVmiNavigation}
                            vmiPageLinks={vmiPageLinks}
                            displayModeSwitch={displayModeSwitch}
                            currentMode={currentMode}
                            rootElementSelector=".mobile-menu-wrapper"
                            allowOpenParentLinks={openParentPages}
                            {...({ showAllSolutions, showFeaturedPartners, xBrandsAndSolutions } as any)}
                            // xng-change: add google event
                            handleClickedLink={(title: string, url: string) => this.getDataLayerEvent(title, url)}
                        />
                    </Hidden>
                    <Hidden className="mobile-search-wrapper" above="sm" {...styles.mobileSearchWrapper}>
                        {/* <Button
                            className="mobile-search-button"
                            {...styles.mobileSearchButton}
                            onClick={this.mobileSearchButtonClickHandler}
                        >
                            <ButtonIcon className="mobile-search-button-icon" src={Search} />
                            <VisuallyHidden>{translate("Search")}</VisuallyHidden>
                        </Button> */}
                    </Hidden>
                </StyledWrapper>
                <Hidden below="lg">
                    {!displayVmiNavigation && !isEducationCustomer && (
                        <StyledWrapper
                            id="main-navigation-container"
                            className="container"
                            {...styles.container}
                            ref={this.container}
                        >
                            <StyledWrapper {...styles.itemsContainerStyledWrapper}>
                                {links.map((link, index) => {
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <StyledWrapper className="item-wrapper" {...styles.itemWrapper} key={index}>
                                            <MainNavigationItem
                                                index={index}
                                                link={link}
                                                styles={styles}
                                                container={this.container}
                                                isOpen={index === selectedLinkIndex}
                                                displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                                // xng-change: add google event
                                                handleClickedLink={(title, url) =>
                                                    this.getDataLayerEvent(title || link.title, url)
                                                }
                                            />
                                        </StyledWrapper>
                                    );
                                })}
                                {/* XNG-CHANGES: Adding brand and solutions menu items */}
                                {showFeaturedPartners && brands && (
                                    <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                        <MainNavigationItem
                                            index="brand"
                                            link={{
                                                title: brands.brandPageTitle,
                                                url: brands.brandPageUrl,
                                                childrenType: "CascadingMenu",
                                                children: brands.headerFeaturedBrands,
                                                headerContent: brands.headerFeaturedBrands,
                                            }}
                                            marketingData={{
                                                marketingGeneralPromotionTitle: brands.marketingGeneralPromotionTitle,
                                                marketingGeneralPromoContent: brands.marketingGeneralPromoContent,
                                                marketingGeneralPromoImage: brands.marketingGeneralPromoImage,
                                                marketingFeaturedProducts: brands.marketingFeaturedProducts,
                                            }}
                                            styles={styles}
                                            container={this.container}
                                            displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                            {...({ pageType: "BrandsPage" } as any)}
                                            // xng-change: add google event
                                            handleClickedLink={(title: string, url: string) =>
                                                this.getDataLayerEvent(title, url)
                                            }
                                        />
                                    </StyledWrapper>
                                )}

                                {showAllSolutions && solutions && (
                                    <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                        <MainNavigationItem
                                            index="solutions"
                                            link={{
                                                title: solutions?.solutionPageTitle ?? translate("Our Solutions"),
                                                url: solutions.solutionPageUrl,
                                                childrenType: "CascadingMenu",
                                                children: solutions.headerSolutions,
                                                headerContent: solutions.headerSolutions,
                                            }}
                                            marketingData={{
                                                marketingGeneralPromotionTitle:
                                                    solutions.marketingGeneralPromotionTitle,
                                                marketingGeneralPromoContent: solutions.marketingGeneralPromoContent,
                                                marketingGeneralPromoImage: solutions.marketingGeneralPromoImage,
                                                marketingFeaturedProducts: solutions.marketingFeaturedProducts,
                                            }}
                                            styles={styles}
                                            container={this.container}
                                            displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                            {...({ pageType: "SolutionsPage" } as any)}
                                            // xng-change: add google event
                                            handleClickedLink={(title: string, url: string) =>
                                                this.getDataLayerEvent(title, url)
                                            }
                                        />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles}>
                                {showQuickOrderLink && quickOrderLink && (
                                    <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                        <StyledWrapper
                                            className="quick-order-item-wrapper"
                                            {...styles.quickOrderItemWrapper}
                                        >
                                            <Link
                                                className="quick-order-link"
                                                typographyProps={styles.menuItemTypography}
                                                color={styles.menuItemTypography?.color}
                                                {...styles.menuItem}
                                                id="quickOrder"
                                                href={quickOrderLink.url}
                                                aria-label={quickOrderLink.title}
                                                // xng-change: add google event
                                                onClick={() =>
                                                    this.getDataLayerEvent(quickOrderLink.title, quickOrderLink.url)
                                                }
                                            >
                                                {quickOrderLink.title}
                                            </Link>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {displayVmiNavigation && (
                        <StyledWrapper className="container" {...styles.container} ref={this.container}>
                            <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                {vmiPageLinks.map(
                                    link =>
                                        link && (
                                            <Link
                                                className="vmi-page-link"
                                                key={link.id}
                                                typographyProps={styles.menuItemTypography}
                                                color={styles.menuItemTypography?.color}
                                                {...styles.menuItem}
                                                id={link.id}
                                                href={link.url}
                                                aria-label={link.title}
                                                // xng-change: add google event
                                                onClick={() => this.getDataLayerEvent(link.title, link.url)}
                                            >
                                                {link.title}
                                            </Link>
                                        ),
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </Hidden>
                <Modal
                    className="mobile-search-modal"
                    {...styles.mobileSearchModal}
                    isOpen={this.state.mobileSearchModalIsOpen}
                    closeOnEsc={true}
                    handleClose={this.mobileSearchModalCloseHandler}
                    headline={
                        <SearchInput
                            id={id}
                            inputRef={this.mobileSearchInput}
                            autocompletePositionFunction={this.getMobileSearchAutocompletePosition}
                            onBeforeGoToUrl={this.mobileSearchModalCloseHandler}
                            extendedStyles={styles.mobileSearchInputStyles}
                        />
                    }
                />
            </StyledWrapper>
        );
    }
}

MainNavigation.contextType = StylesContext;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const mainNavigation: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MainNavigation),
    definition: {
        group: "Header",
        icon: "magnifying-glass",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                onEditRow: (index: number, dispatch) => {
                    dispatch({
                        type: "SendToSite/OpenMainNavigation",
                        index,
                    });
                },
                onDoneEditingRow: dispatch => {
                    dispatch({
                        type: "SendToSite/CloseMainNavigation",
                    });
                },
                onLoad: dispatch => {
                    dispatch({
                        type: "SendToSite/CloseMainNavigation",
                    });
                },
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overrideTitle, title } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overrideTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overrideTitle) {
                            return overrideTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "linkType",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "Link",
                        isRequired: true,
                        options: [
                            {
                                displayName: "Link",
                                value: "Link",
                            },
                            {
                                displayName: "Mega Menu",
                                value: "MegaMenu",
                            },
                            {
                                displayName: "Cascading Menu",
                                value: "CascadingMenu",
                            },
                        ],
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowUrls: item => item.fields.linkType === "Link",
                        showCategoryRoot: item => true,
                    },
                    {
                        name: "overrideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.destination.type !== "Url",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        isVisible: item => item.fields.destination.type === "Url",
                    },
                    {
                        name: "numberOfColumns",
                        editorTemplate: "DropDownField",
                        defaultValue: 6,
                        options: [{ value: 2 }, { value: 3 }, { value: 4 }, { value: 6 }],
                        isRequired: true,
                        isVisible: (item: HasFields) => item.fields.linkType === "MegaMenu",
                    },
                    {
                        name: "maxDepth",
                        editorTemplate: "DropDownField",
                        defaultValue: 3,
                        options: [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }],
                        isRequired: true,
                        isVisible: (item: HasFields) => item.fields.linkType === "CascadingMenu",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: (item: HasFields) => item.fields.linkType === "Link",
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.showFeaturedPartners,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.showAllSolutions,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.showQuickOrder,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.openParentPages,
                displayName: "Open Parent Pages",
                tooltip:
                    "When enabled, tapping on a text link in the flyout menu will directly open the linked webpage and require users to click the chevron to access child pages. If disabled, tapping any link in the flyout menu will navigate to a submenu of child pages.",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default mainNavigation;
